import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button, Input, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';

import * as userUrls from '../../urls/api-users/admin';
import { isLocalLikeHost } from '../../utils/environmentMode';

const LOCAL_BROWSER_USERS_HOST = 'http://127.0.0.1:8082';
const PARTNER_REQUEST_TIMEOUT_MS = 10000;

const resolvePartnerUsersHost = () => {
  const browserHost = typeof window !== 'undefined' && window.location ? window.location.hostname : '';

  if (isLocalLikeHost(browserHost) && !isLocalLikeHost(userUrls.usersHost)) {
    return LOCAL_BROWSER_USERS_HOST;
  }

  return userUrls.usersHost;
};

const buildReferralPartnerUrl = identifier =>
  `${resolvePartnerUsersHost()}/admin/referral-partners/${encodeURIComponent(identifier)}`;

const buildHostedReferralPartnerUrl = identifier =>
  `${userUrls.hostedUsersHost}/admin/referral-partners/${encodeURIComponent(identifier)}`;

const getPartnerErrorMessage = (error, fallbackMessage) => {
  const rawMessage = error && error.response && error.response.data
    ? (error.response.data.debug || error.response.data.message)
    : fallbackMessage;

  if (typeof rawMessage === 'string' && /SequelizeConnectionError|ETIMEDOUT|ECONNREFUSED|connect\s+/i.test(rawMessage)) {
    return fallbackMessage;
  }

  return rawMessage || fallbackMessage;
};

const requestPartnerAdmin = async ({ identifier, method = 'get', data = undefined }) => {
  const primaryUrl = buildReferralPartnerUrl(identifier);
  const fallbackUrl = buildHostedReferralPartnerUrl(identifier);
  const requestConfig = {
    method,
    url: primaryUrl,
    timeout: PARTNER_REQUEST_TIMEOUT_MS,
    headers: {
      Authorization: 'Bearer ' + localStorage.getItem('access_token'),
      'content-type': 'application/json',
    },
    ...(typeof data !== 'undefined' ? { data } : {}),
  };

  const shouldRetryHosted = error => {
    const status = error && error.response ? error.response.status : null;

    if (!fallbackUrl || fallbackUrl === primaryUrl) {
      return false;
    }

    if (status === 401 || status === 403 || status === 404) {
      return false;
    }

    if (status === null) {
      return true;
    }

    return status >= 500;
  };

  try {
    return await axios(requestConfig);
  } catch (error) {
    if (!shouldRetryHosted(error)) {
      throw error;
    }

    return axios({
      ...requestConfig,
      url: fallbackUrl,
    });
  }
};

const panelStyles = {
  container: {
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: '12px',
    padding: '12px',
    background: 'rgba(39, 41, 61, 0.55)',
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: 'fit-content',
    maxWidth: '100%',
  },
  heading: {
    marginBottom: '8px',
  },
  dropdownRow: {
    marginBottom: '10px',
  },
  copy: {
    fontSize: '0.88rem',
    color: 'rgba(255, 255, 255, 0.72)',
    marginBottom: '10px',
    maxWidth: '340px',
  },
  commissionRow: {
    minWidth: '120px',
    marginBottom: '0',
  },
  label: {
    display: 'block',
    fontSize: '0.85rem',
    marginBottom: '6px',
  },
  dropdownButton: {
    minWidth: '200px',
    textAlign: 'left',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statusCopy: {
    fontSize: '0.86rem',
    marginBottom: '6px',
  },
  detailsGrid: {
    display: 'grid',
    gap: '6px',
    fontSize: '0.86rem',
  },
  detailsItem: {
    marginBottom: 0,
  },
  modalDialog: {
    maxWidth: '240px',
    width: '240px',
  },
  modalBody: {
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    textAlign: 'center',
  },
  modalFooter: {
    justifyContent: 'center',
    gap: '8px',
    paddingTop: '0.5rem',
  },
  modalTitle: {
    width: '100%',
    textAlign: 'center',
  },
  commissionFieldWrap: {
    position: 'relative',
    width: '112px',
    margin: '0 auto',
  },
  commissionInput: {
    color: '#111111',
    backgroundColor: '#ffffff',
    width: '112px',
    paddingRight: '30px',
    textAlign: 'center',
  },
  commissionSuffix: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#111111',
    fontSize: '0.95rem',
    fontWeight: 600,
    pointerEvents: 'none',
  },
  detailValue: {
    color: 'rgba(255, 255, 255, 0.82)',
  },
};

const buildDefaultPartnerState = () => ({
  active_qr_mode: 'referral_partner',
  referral_partner: null,
  affiliate_partner: null,
});

const DriverPartnerPanel = ({ driverAuth0Id, driverId, driverContext = null, title = 'Driver Partner Account', showCopy = false, containerStyle = null }) => {
  const [partnerAccounts, setPartnerAccounts] = useState(null);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [partnerSaving, setPartnerSaving] = useState(false);
  const [partnerMode, setPartnerMode] = useState('referral_partner');
  const [partnerCommissionPercent, setPartnerCommissionPercent] = useState('5');
  const [partnerError, setPartnerError] = useState('');
  const [partnerSuccess, setPartnerSuccess] = useState('');
  const [affiliateModalOpen, setAffiliateModalOpen] = useState(false);
  const isMountedRef = useRef(false);
  const latestFetchRequestRef = useRef(0);

  const modeLabel = partnerMode === 'affiliate_partner' ? 'Affiliate' : 'Referral Partner';
  const driverLookupKey = driverId || driverAuth0Id;

  const syncDriverPartnerState = payload => {
    if (!isMountedRef.current) {
      return;
    }

    setPartnerAccounts(payload);
    const selectedMode = payload.active_qr_mode === 'sales_affiliate' ? 'affiliate_partner' : 'referral_partner';
    setPartnerMode(selectedMode);
    const affiliateRate = payload.affiliate_partner && payload.affiliate_partner.commission_rate != null ? payload.affiliate_partner.commission_rate : 0.05;
    setPartnerCommissionPercent(String(Number(affiliateRate) * 100));
  };

  const fetchDriverPartnerAccounts = identifier => {
    const requestId = latestFetchRequestRef.current + 1;
    latestFetchRequestRef.current = requestId;

    setPartnerLoading(true);
    setPartnerError('');
    setPartnerSuccess('');
    requestPartnerAdmin({
      identifier,
      method: 'get',
    })
      .then(response => {
        if (!isMountedRef.current || latestFetchRequestRef.current !== requestId) {
          return;
        }

        syncDriverPartnerState(response.data.payload);
      })
      .catch(error => {
        if (!isMountedRef.current || latestFetchRequestRef.current !== requestId) {
          return;
        }

        const statusCode = error && error.response ? error.response.status : null;
        const message = getPartnerErrorMessage(error, 'Unable to load driver referral settings');
        if (statusCode === 404 || message === 'Driver not found') {
          syncDriverPartnerState(buildDefaultPartnerState());
          return;
        }

        setPartnerError(message);
      })
      .finally(() => {
        if (!isMountedRef.current || latestFetchRequestRef.current !== requestId) {
          return;
        }

        setPartnerLoading(false);
      });
  };

  const saveDriverPartnerMode = (modeOverride, commissionOverride) => {
    if (!driverLookupKey) {
      return;
    }

    setPartnerSaving(true);
    setPartnerError('');
    setPartnerSuccess('');

    const nextMode = modeOverride || partnerMode;
    const payload = { mode: nextMode };
    if (driverContext) {
      payload.driver_context = driverContext;
    }
    if (nextMode === 'affiliate_partner') {
      const nextCommissionPercent = Number(commissionOverride != null ? commissionOverride : partnerCommissionPercent);
      if (Number.isNaN(nextCommissionPercent) || nextCommissionPercent < 0 || nextCommissionPercent > 100) {
        setPartnerSaving(false);
        setPartnerError('Commission percent must be between 0 and 100');
        return;
      }

      payload.commission_rate = nextCommissionPercent / 100;
    }

    requestPartnerAdmin({
      identifier: driverLookupKey,
      method: 'put',
      data: payload,
    })
      .then(response => {
        if (!isMountedRef.current) {
          return;
        }

        syncDriverPartnerState(response.data.payload);
        setPartnerSuccess('Driver partner mode updated');
        setAffiliateModalOpen(false);
      })
      .catch(error => {
        if (!isMountedRef.current) {
          return;
        }

        const message = getPartnerErrorMessage(error, 'Unable to update driver referral settings');
        setPartnerError(message);
      })
      .finally(() => {
        if (!isMountedRef.current) {
          return;
        }

        setPartnerSaving(false);
      });
  };

  const handleModeSelect = mode => {
    setPartnerError('');
    setPartnerSuccess('');

    if (mode === 'affiliate_partner') {
      setPartnerMode(mode);
      setAffiliateModalOpen(true);
      return;
    }

    setPartnerMode(mode);
    saveDriverPartnerMode(mode);
  };

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (driverLookupKey) {
      fetchDriverPartnerAccounts(driverLookupKey);
    } else {
      syncDriverPartnerState(buildDefaultPartnerState());
    }
  }, [driverLookupKey]);

  const activeQrModeLabel = partnerAccounts && partnerAccounts.active_qr_mode === 'sales_affiliate' ? 'Affiliate' : 'Referral Partner';

  return (
    <div style={{ ...panelStyles.container, ...(containerStyle || {}) }}>
      <h4 style={panelStyles.heading}>{title}</h4>
      {showCopy ? (
        <p style={panelStyles.copy}>Every driver starts as a referral partner. Toggle to affiliate when you want the driver QR flow and rate to switch to affiliate mode.</p>
      ) : null}
      <div style={panelStyles.dropdownRow}>
        <UncontrolledDropdown>
          <DropdownToggle caret className="btn btn-default" style={panelStyles.dropdownButton} disabled={partnerLoading || partnerSaving}>
            {modeLabel}
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem onClick={() => handleModeSelect('referral_partner')}>Referral Partner</DropdownItem>
            <DropdownItem onClick={() => handleModeSelect('affiliate_partner')}>Affiliate</DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      </div>

      {partnerLoading ? <p style={panelStyles.statusCopy}>Loading partner settings...</p> : null}
      {partnerError !== '' ? <p style={{ ...panelStyles.statusCopy, color: '#ff6b6b' }}>{partnerError}</p> : null}
      {partnerSuccess !== '' ? <p style={{ ...panelStyles.statusCopy, color: '#2ecc71' }}>{partnerSuccess}</p> : null}

      {partnerAccounts ? (
        <div style={panelStyles.detailsGrid}>
          <p style={panelStyles.detailsItem}>
            <strong>Active QR Mode: </strong>
            <span style={panelStyles.detailValue}>{activeQrModeLabel}</span>
          </p>
          <p style={panelStyles.detailsItem}>
            <strong>Rate: </strong>
            <span style={panelStyles.detailValue}>
              {partnerAccounts.active_qr_mode === 'sales_affiliate'
                ? `${(Number((partnerAccounts.affiliate_partner && partnerAccounts.affiliate_partner.commission_rate) || 0.05) * 100).toFixed(2)}%`
                : '1.00%'}
            </span>
          </p>
        </div>
      ) : null}

      <Modal isOpen={affiliateModalOpen} toggle={() => setAffiliateModalOpen(!affiliateModalOpen)} centered style={panelStyles.modalDialog}>
        <ModalHeader toggle={() => setAffiliateModalOpen(!affiliateModalOpen)} style={panelStyles.modalTitle}>Set Affiliate Commission</ModalHeader>
        <ModalBody style={panelStyles.modalBody}>
          <div style={panelStyles.commissionRow}>
            <label style={{ ...panelStyles.label, textAlign: 'center' }}>Affiliate commission percent</label>
            <div style={panelStyles.commissionFieldWrap}>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.25"
                value={partnerCommissionPercent}
                onChange={e => setPartnerCommissionPercent(e.target.value)}
                disabled={partnerSaving}
                style={panelStyles.commissionInput}
              />
              <span style={panelStyles.commissionSuffix}>%</span>
            </div>
            <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '0.85rem', color: '#525f7f', textAlign: 'center' }}>Enter `5` for 5% commission.</p>
          </div>
        </ModalBody>
        <ModalFooter style={panelStyles.modalFooter}>
          <Button className="btn btn-default" onClick={() => setAffiliateModalOpen(false)} disabled={partnerSaving}>
            Cancel
          </Button>
          <Button className="btn btn-success" onClick={() => saveDriverPartnerMode('affiliate_partner', partnerCommissionPercent)} disabled={partnerSaving}>
            {partnerSaving ? 'Saving...' : 'Save'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default DriverPartnerPanel;